import React, { useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes";
import { RoutesType } from "types/globalTypes";
import LayoutGuard from "components/guards/LayoutGuard";

export default function MainLayout(props: {
  [x: string]: any;
  path: "admin" | "company" | "organization";
}) {
  const { path, ...rest } = props;
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    if (window.innerWidth >= 1024) {
      setOpen(true);
    }

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCloseSidebar = () => {
    if (!isDesktop) {
      setOpen(false);
    }
  };

  // Memoize filtered routes for current layout
  const currentLayoutRoutes = useMemo(() => {
    const currentLayout = `/${path}`;
    const filteredRoutes: RoutesType[] = [];

    routes.forEach((route) => {
      if (route.layout === currentLayout) {
        filteredRoutes.push(route);
      }

      if (route.children) {
        const filteredChildren = route.children.filter(
          (child) => child.layout === currentLayout
        );
        if (filteredChildren.length > 0) {
          const existingRoute = filteredRoutes.find(
            (r) => r.path === route.path
          );
          if (existingRoute) {
            existingRoute.children = filteredChildren;
          } else {
            filteredRoutes.push({
              ...route,
              children: filteredChildren,
            });
          }
        }
      }
    });

    return filteredRoutes;
  }, [path]);

  const getActiveNavbar = (routes: RoutesType[]): string | boolean => {
    let activeNavbar = false;
    const currentPath = location.pathname;

    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      if (
        route.layout &&
        route.path &&
        currentPath.includes(`${route.layout}/${route.path}`)
      ) {
        return route.secondary || false;
      }
      if (route.children) {
        for (const child of route.children) {
          if (
            child.layout &&
            child.path &&
            currentPath.includes(`${child.layout}/${child.path}`)
          ) {
            return child.secondary || false;
          }
        }
      }
    }
    return activeNavbar;
  };

  const renderRoutes = (routes: RoutesType[]): React.ReactNode => {
    const elements: React.ReactNode[] = [];

    routes.forEach((route, index) => {
      if (route.path && route.component) {
        elements.push(
          <Route
            key={`route-${index}`}
            path={`/${route.path}`}
            element={route.component}
          />
        );
      }

      if (route.children && route.children.length > 0) {
        route.children.forEach((child, childIndex) => {
          if (child.path && child.component) {
            elements.push(
              <Route
                key={`child-${index}-${childIndex}`}
                path={`/${child.path}`}
                element={child.component}
              />
            );
          }
        });
      }
    });

    return elements;
  };

  return (
    <LayoutGuard layoutType={path}>
      <div className="flex min-h-screen w-full">
        <Sidebar
          open={open}
          onClose={handleCloseSidebar}
          onCloseSidebar={handleCloseSidebar}
        />
        <div className="min-h-screen w-full bg-lightPrimary dark:!bg-navy-900">
          <main
            className={`mx-[12px] min-h-screen flex-none transition-all md:pr-2 xl:ms-[280px]`}
          >
            <div className="min-h-screen">
              <Navbar
                onOpenSidenav={() => setOpen(true)}
                secondary={getActiveNavbar(routes)}
                {...rest}
              />
              <div className="mx-auto mb-auto min-h-[calc(100vh-200px)] p-2 pt-5 md:pr-2">
                <Routes>
                  {renderRoutes(currentLayoutRoutes)}
                  <Route
                    path="/"
                    element={
                      <Navigate
                        to={
                          path === "admin"
                            ? "/admin/dashboard"
                            : path === "company"
                            ? "/company/dashboard"
                            : "/organization/dashboard"
                        }
                        replace
                      />
                    }
                  />
                </Routes>
              </div>
              <div className="p-3">
                <Footer />
              </div>
            </div>
          </main>
        </div>
      </div>
    </LayoutGuard>
  );
}
